import { booleanAttribute, Component, computed, input, output } from '@angular/core';

import { MibPalette } from '../../foundations';
import { MIB_PALETTE_DOT } from '../badge';
import { MibButtonDirective, ButtonSize } from '../button';
import { MibIconComponent } from '../icons';
import { MibFilterChip, MibFilterChipsAppearance } from './filter-chips.models';

const SOLID_CHIP_CLASSES = [
  'border-transparent bg-gray-100 enabled:text-gray-900 enabled:hover:bg-gray-200',
  'enabled:aria-pressed:bg-mib-branding-900 enabled:aria-pressed:text-white',
  'enabled:aria-pressed:hover:bg-mib-branding-700',
  'dark:bg-gray-800 dark:enabled:text-gray-100 dark:enabled:hover:bg-gray-700',
  'dark:enabled:aria-pressed:bg-mib-branding-50 dark:enabled:aria-pressed:text-mib-branding-900',
  'dark:enabled:aria-pressed:hover:bg-mib-branding-100',
].join(' ');

/**
 * Row of chips a list is filtered with: one per kind, pressed or not, several or one at a time.
 *
 * A chip is a `secondary` button on the `round` shape, which the `solid` appearance only recolours:
 * the row draws them rather than declaring a skin of its own, so a chip in a row and a chip written
 * by hand outside one stay the same object. What the row adds is the rule of a filter: which chips
 * are pressed, and what pressing one does to the others.
 *
 * The component is controlled: it never mutates {@link value}, it emits the selection the caller
 * should apply. A row and the list it filters cannot drift apart that way.
 *
 * Each chip is its own tab stop, unlike the roving tab stop of `<mib-button-toggles>`: the segments
 * of a group are one control with several faces, where the chips of a filter are choices that
 * happen to sit side by side, and a reader skipping the row with Tab has skipped nothing else.
 *
 * The row pins one thing the button does not give it: **a pressed chip answers the pointer**. The
 * button holds `active` through hover on purpose — a trigger whose panel is open should not flinch
 * under the cursor — but a pressed chip is something a reader is about to release, so it goes one
 * step deeper instead of staying still. A utility beats the skin's own layer, which is why this is
 * one class rather than a thirteenth colour slot on every button of the library.
 */
@Component({
  selector: 'mib-filter-chips',
  templateUrl: './filter-chips.component.html',
  imports: [MibButtonDirective, MibIconComponent],
  host: {
    role: 'group',
    class: 'mib-scrollbar-none flex items-center gap-2 overflow-x-auto overscroll-x-contain',
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel()',
  },
})
export class MibFilterChipsComponent {
  /** The kinds the list can be narrowed by, in the order they are drawn. */
  readonly chips = input.required<MibFilterChip[]>();
  /** Ids of the kinds currently pressed. Empty means no filter — and presses the `all` chip. */
  readonly value = input.required<readonly string[]>();
  /** Accessible name of the row, e.g. "Filter by specialty". */
  readonly ariaLabel = input.required<string>({ alias: 'aria-label' });
  /** Height of every chip, the button's own steps. */
  readonly size = input<ButtonSize>('md');
  /** Disables the whole row, on top of the per-chip `disabled` flag. */
  readonly disabled = input(false, { transform: booleanAttribute });
  /** Several kinds at a time; `false` makes the row a single choice, the pressed chip alone. */
  readonly multiple = input(true, { transform: booleanAttribute });
  /** On a single choice, pressing the pressed chip again clears the choice. */
  readonly deselectable = input(false, { transform: booleanAttribute });
  /** How the chips are drawn: the bordered secondary pill, or a grey fill that turns navy when pressed. */
  readonly appearance = input<MibFilterChipsAppearance>('outlined');

  /** Emits the new selection, in the declared chip order. */
  readonly valueChange = output<string[]>();

  protected readonly hostClasses = computed(() =>
    this.appearance() === 'outlined' ? '[&>button[aria-pressed=true]:hover]:bg-gray-300' : '',
  );

  /** Utilities beat the button skin's layer; `enabled:` leaves a disabled chip's ink to the skin. */
  protected readonly chipClasses = computed(() => (this.appearance() === 'solid' ? SOLID_CHIP_CLASSES : ''));

  protected isPressed(chip: MibFilterChip): boolean {
    return chip.all ? this.value().length === 0 : this.value().includes(chip.id);
  }

  protected isFavouritePressed(chip: MibFilterChip): boolean {
    return chip.pressedIcon === 'favourite' && this.isPressed(chip);
  }

  protected iconClasses(chip: MibFilterChip): string {
    return this.isFavouritePressed(chip) ? 'size-4 text-mib-favourite' : 'size-4';
  }

  protected dotClasses(palette: MibPalette): string {
    return `size-2 shrink-0 rounded-full ${MIB_PALETTE_DOT[palette]}`;
  }

  /**
   * Nothing is emitted when the press changes nothing, so a page holding the selection in a signal
   * is never handed a new array that means what the old one meant.
   */
  protected press(chip: MibFilterChip): void {
    const selected = this.value();

    if (chip.all) {
      if (selected.length > 0) {
        this.valueChange.emit([]);
      }
      return;
    }

    if (!this.multiple()) {
      this.pressAlone(chip, selected);
      return;
    }

    if (selected.includes(chip.id)) {
      this.valueChange.emit(selected.filter((id) => id !== chip.id));
      return;
    }

    /*
     Emitted in the declared order rather than in the order pressed: the selection is a set, and a
     page that shows it back — a sentence, a count, a second row — should not see it reshuffle.
    */
    this.valueChange.emit(
      this.chips()
        .filter(({ id, all }) => !all && (id === chip.id || selected.includes(id)))
        .map(({ id }) => id),
    );
  }

  /** A single choice is a radio: pressing the pressed chip again keeps it pressed, unless {@link deselectable}. */
  private pressAlone(chip: MibFilterChip, selected: readonly string[]): void {
    if (selected.length === 1 && selected[0] === chip.id) {
      if (this.deselectable()) {
        this.valueChange.emit([]);
      }
      return;
    }
    this.valueChange.emit([chip.id]);
  }
}
